import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Phone, CheckCircle, Calendar, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import CostSimulator from '../components/CostSimulator';
import { SEOHead, localBusinessSchema, faqSchema } from '../components/SEOHead';
import { AGENCIES } from '../lib/agencies-data';
import { getZones, getMainCities, SAP_NUMBER } from '../lib/cities-data';
import { SERVICE_CITY_CONFIG, serviceCityPath } from '../lib/service-city-data';
import { COMPANY_INFO, formatPrice } from '../lib/utils';
import { VIEWPORT_ONCE, SCALE_IN_INITIAL, SCALE_IN_ANIMATE, SCALE_IN_TRANSITION_SLOW } from '../lib/motion';

const SITE_URL = 'https://ptitservices.com';
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function ServiceHubPage({ serviceKey }) {
  const cfg = SERVICE_CITY_CONFIG[serviceKey];
  const agency = AGENCIES['artois.ptitservices'];
  if (!cfg || !cfg.hub) return <Navigate to="/services" replace />;

  const hub = cfg.hub;
  const Icon = cfg.icon;
  const zones = getZones();
  const main = getMainCities();
  const faq = cfg.faq('votre commune', "l'Artois");

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}${cfg.basePath}`,
    "name": `${cfg.label} dans l'Artois`,
    "serviceType": cfg.serviceType || cfg.label,
    "description": hub.description,
    "url": `${SITE_URL}${cfg.basePath}`,
    "provider": { ...localBusinessSchema(agency), "@context": undefined },
    "areaServed": main.map((c) => ({ "@type": "City", "name": c.name })),
    "offers": { "@type": "Offer", "price": cfg.priceAfter, "priceCurrency": "EUR", "priceSpecification": { "@type": "UnitPriceSpecification", "price": cfg.priceAfter, "priceCurrency": "EUR", "unitText": "heure" } },
  };

  return (
    <div data-testid={`service-hub-${serviceKey}`}>
      <SEOHead title={hub.title} description={hub.description} path={cfg.basePath} schema={[schema, faqSchema(faq)]} />

      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-foreground">Accueil</Link><span>/</span>
            <Link to="/services" className="hover:text-foreground">Services</Link><span>/</span>
            <span className="text-foreground font-medium">{cfg.label}</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div className="lg:col-span-7" initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp}>
                <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Icon className="h-4 w-4 mr-2" /> {cfg.label} — Artois</Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" data-testid="service-hub-title">{hub.h1}</motion.h1>
              <motion.p variants={fadeUp} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">{hub.intro}</motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm">
                  <span className="text-xs text-muted-foreground block">Régulier — à partir de</span>
                  <span className="text-sm line-through text-muted-foreground">{formatPrice(cfg.priceBefore)}/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">{formatPrice(cfg.priceAfter)}/h</span>
                </div>
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm">
                  <span className="text-xs text-muted-foreground block">Ponctuel sans engagement</span>
                  <span className="text-sm line-through text-muted-foreground">{formatPrice(cfg.pricePonctuel)}/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">{formatPrice(cfg.pricePonctuel / 2)}/h</span>
                </div>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/devis" state={{ service: cfg.quoteService }} data-testid="service-hub-quote-button"><Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">Devis gratuit <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`}><Button size="lg" variant="outline"><Phone className="mr-2 h-4 w-4" /> {COMPANY_INFO.phone}</Button></a>
              </motion.div>
            </motion.div>
            <motion.div className="lg:col-span-5" initial={SCALE_IN_INITIAL} animate={SCALE_IN_ANIMATE} transition={SCALE_IN_TRANSITION_SLOW}>
              <Card className="p-6 shadow-xl">
                <h2 className="font-['Space_Grotesk'] font-semibold text-lg mb-4 flex items-center gap-2"><MapPin className="h-5 w-5 text-[hsl(var(--primary))]" /> Votre commune</h2>
                <p className="text-sm text-muted-foreground mb-3">Choisissez votre ville pour voir le détail local, les tarifs et la FAQ :</p>
                <div className="flex flex-wrap gap-2" data-testid="service-hub-main-cities">
                  {main.map((c) => (
                    <Link key={c.slug} to={serviceCityPath(serviceKey, c)} className="px-3 py-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors">{c.name}</Link>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Déclaré Services à la Personne — {SAP_NUMBER}</p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content sections */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {hub.sections.map((sec) => (
            <motion.div key={sec.title} initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Card className="p-6 h-full">
                <h2 className="font-['Space_Grotesk'] text-xl font-semibold mb-4 flex items-center gap-2"><Calendar className="h-5 w-5 text-[hsl(var(--primary))]" /> {sec.title}</h2>
                <ul className="space-y-2.5">
                  {sec.items.map((it) => <li key={it} className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed"><CheckCircle className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />{it}</li>)}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Prestations */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-8 text-center">{cfg.prestationsTitle("dans l'Artois").replace("à dans", "dans")}</h2>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {cfg.prestations.map((p) => (
              <motion.div key={p.title} variants={fadeUp}>
                <Card className="p-5 h-full"><CheckCircle className="h-5 w-5 text-[hsl(var(--primary))] mb-2" /><h3 className="font-semibold text-sm mb-1">{p.title}</h3><p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p></Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Simulator + FAQ */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Questions fréquentes</h2>
            <Accordion type="single" collapsible className="space-y-3" data-testid="service-hub-faq">
              {faq.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                  <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <CostSimulator preselectedTab={cfg.simulatorTab} />
        </div>
      </section>

      {/* All cities */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mb-2">{cfg.label} : toutes les communes desservies</h2>
          <p className="text-sm text-muted-foreground mb-6">40 km autour de Servins — cliquez sur votre commune.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="service-hub-zones">
            {zones.map((zone) => (
              <Card key={zone.region} className="p-5">
                <h3 className="font-semibold text-sm mb-3 text-[hsl(var(--primary))]">{zone.region}</h3>
                <ul className="flex flex-wrap gap-1.5">
                  {zone.cities.map((c) => <li key={c.slug}><Link to={serviceCityPath(serviceKey, c)} className="inline-block text-xs px-2.5 py-1 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors">{c.name}</Link></li>)}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
